import * as React from 'react';

import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '../lib/utils';

const buttonVariants = cva(
  [
    'relative inline-flex items-center justify-center',
    'whitespace-nowrap transition-colors',
    // #1329 T19b: Fokus = der globale 2-px-Umriss in primary mit 2 px Abstand
    // (globals.css, subcontracts `:focus-visible` petrol-2) — kein eigener Ring.

    // 🔴 A2: „Trefferflaeche >= 44 px. Gilt auch in der Desktop-Ansicht — sie
    // laeuft auf dem iPad. Knoepfe duerfen 38 px hoch aussehen, brauchen aber
    // 44 px Trefferflaeche."
    //
    // Beides zugleich geht nur ueber eine unsichtbare Flaeche: `::after` liegt
    // mittig ueber dem Knopf und ist 44 px hoch. Sie gehoert zum Knopf, nimmt
    // also seine Zeigerereignisse entgegen — der Knopf bleibt optisch 38 px.
    // Die Alternative (den Knopf auf 44 px hochziehen) waere ein anderer
    // Entwurf, nicht dieselbe Umsetzung.
    //
    // 🔴 NICHT bei `variant="link"`. Ein Schriftlink ist rund 20 px hoch und
    // steht mitten im Satz — dort ist das Band 44 px hoch, unsichtbar, liegt
    // per `position: relative` UEBER dem Nachbarinhalt und faengt dessen
    // Klicks ab. Belegt in der Tabu-Zone: Anmeldung, Registrierung,
    // Passwort-Zuruecksetzen und zwei Onboarding-Schritte nutzen
    // `variant="link" className="h-auto p-0"`. A2 verlangt 44 px fuer KNOEPFE;
    // ein Textlink in einer Zeile ist kein Knopf, und ein gestohlener Klick am
    // Nachbarelement ist schlimmer als eine kleinere Trefferflaeche.
    // Der Ausschluss steht deshalb in der `link`-Variante selbst (`after:hidden`).
    "after:absolute after:inset-x-0 after:top-1/2 after:h-11 after:-translate-y-1/2 after:content-['']",

    // 🔴 A2: deaktiviert ueber FARBE, nie ueber `opacity`. Transparenz dimmt den
    // Text mit und drueckt ihn unter 4,5:1 Kontrast — aus „nicht jetzt" wird
    // „nicht lesbar". Nur Flaechen und Rahmen werden heller, der Text bleibt auf
    // --disabled-foreground.
    'disabled:pointer-events-none disabled:border-transparent',
    'disabled:bg-muted disabled:text-disabled-foreground disabled:shadow-none',
  ].join(' '),
  {
    variants: {
      variant: {
        /*
          #1329 — das Knopf-System von subcontracts: drei Arten, eckig, der
          Akzent ist Handlungsfarbe (Auftraggeber 05.10.2026). Kein Schatten
          (A2): ein Knopf liegt IM Inhalt.

          primary  Akzent-Fläche — EINE Hauptaktion je Fläche
          akzent   2-px-Akzent-Rahmen — wichtige Zweitaktion (subcontracts
                   `.btn-outline`); Hover dunkelt den Text auf emphasis-hover
                   (5,16:1 statt 4,00 auf der Tönung)
          quiet    2-px-Haarrahmen — alle übrigen Aktionen

          🔴 `outline` ist NICHT der Akzent-Rahmen, obwohl subcontracts ihn so
          nennt: 293 Bestandsstellen schreiben `outline` und meinten den
          ruhigen Rahmen. Hätte der Name die Bedeutung gewechselt, stünden
          Filterpillen und Zeilenknöpfe auf einen Schlag in Vermillion
          (gemessen an der Vorher/Nachher-Aufnahme, 05.10.2026) — gegen die
          Regel „eine Akzentfläche je Fläche".
          ghost    ohne Rahmen — Symbolknöpfe, Werkzeugzeilen
          danger   Rahmen und Text in der Fehlerfarbe, nie als volle Fläche
                   (der Fehler teilt den Farbton mit dem Akzent)
        */
        primary:
          'bg-emphasis text-emphasis-foreground hover:bg-emphasis-hover border-2 border-transparent',
        akzent:
          'border-emphasis bg-card text-emphasis hover:bg-emphasis-subtle hover:text-emphasis-hover border-2',
        quiet:
          'border-border bg-card text-foreground hover:border-border-accent border-2',
        ghost: 'text-foreground hover:bg-tint',
        danger:
          'border-danger bg-card text-danger hover:bg-danger-subtle border-2',
        // `disabled:bg-transparent` (T18): ein Textverweis hat keine Fläche —
        // die Grundklasse setzte gesperrt sonst einen grauen Kasten hinter
        // das Wort.
        link: 'hover:text-primary underline decoration-current/30 underline-offset-[3px] after:hidden hover:decoration-current disabled:bg-transparent',
      },
      /*
        ═══════════════════════════════════════════════════════════════════════
        🔴 VIER MASSE, SONST KEINE — Korrekturpaket 78, Anweisung 506
        ═══════════════════════════════════════════════════════════════════════

        Gemessen ueber alle Blaetter: 313 Knoepfe trugen **16 verschiedene
        Hoehen** (28 bis 56 px), 8 Radien, 13 Schriftgroessen und 5 Gewichte.
        Und der schwerste Teil:

            „39 mobile Knoepfe waren unter 44 px — das ist die harte Grenze aus
             M1, und sie stand seit der ersten Runde im Blatt. Ein 32-px-Knopf
             am Telefon ist nicht knapp, er ist unbedienbar."

        Es gilt, abgeleitet aus BREITE und RANG — nicht aus einem Variantennamen:

          · Rechner, Haupthandlung   40 px · 14 px · 650 · Radius 8
          · Rechner, Nebenhandlung   32 px · 13 px · 600 · Radius 8
          · Mobil,   Haupthandlung   50 px · 16 px · 650 · Radius 10
          · Mobil,   Nebenhandlung   44 px · 15 px · 600 · Radius 10

        „Warum mobil Radius 10 statt 8: bei 50 px Hoehe wirkt 8 px eckig; das
        Verhaeltnis bleibt so gleich."

        🔴 DIE BREITE ENTSCHEIDET, NICHT DER AUFRUFER. Deshalb traegt jede
        Groesse BEIDE Werte und schaltet bei 768 px um — nicht zwei Varianten,
        die jemand von Hand waehlen muesste. Wer waehlen muss, waehlt irgendwann
        falsch; genau daraus sind die 16 Hoehen entstanden. (768 px ist einer
        der zwei erlaubten Umschaltpunkte, `check:zwei-umschaltpunkte`.)

        🔴 `rounded`, `text-` und `font-` sind aus der Grundklasse HERAUSGEZOGEN
        und stehen je Groesse. In der Grundklasse waeren sie ein zweiter Wert
        derselben Eigenschaft: `buttonVariants()` wird auch direkt aufgerufen
        (an `<Link>`-Elementen), und dort laeuft KEIN `twMerge` darueber —
        welcher gewinnt, entschiede dann die Reihenfolge im Stylesheet.

        ═══════════════════════════════════════════════════════════════════════
        🔴 HOEHE, RADIUS UND SCHRIFTGRAD LIEGEN SEIT ANWEISUNG 514 IN DER
        `components`-EBENE — damit der Aufrufer sie ueberschreiben KANN
        ═══════════════════════════════════════════════════════════════════════

        Hier stand das Breiten-Paar direkt an der Groesse
        (`md:rounded-control rounded-control-mobil h-[50px] … md:h-10 md:text-sm`).
        Das hat jeden Aufrufer ueberstimmt, und zwar nur oberhalb von 768 px:
        ein Chip mit `rounded-pill` war am Telefon rund und am Rechner eckig.
        Der Bestand half sich damit, `rounded-pill` ZWEIMAL zu schreiben — je
        einmal mit und ohne Vorsatz. Elf solche Stellen in neun Dateien; sie
        sind mit dieser Aenderung zurueckgebaut.

        514 nennt das eine Notmassnahme am Symptom und die Regel dazu: „ein
        Bauteil setzt seine Maesse so, dass ein Aufrufer sie ueberschreiben
        kann."

        🔴 UND `twMerge` LOEST DAS NICHT — gemessen, nicht vermutet:

            cn('md:rounded-control rounded-control-mobil', 'rounded-pill')
              → "md:rounded-control rounded-control-mobil rounded-pill"
            cn('rounded-control', 'rounded-pill')
              → "rounded-control rounded-pill"

        `tailwind-merge` kennt unsere eigenen Theme-Marken nicht und raeumt
        deshalb GAR NICHTS auf, auch ohne `md:` nicht. Es entscheidet die
        Kaskade. Deshalb liegt die Voreinstellung jetzt in `@layer components`
        (`styles/theme.utilities.css`, `ds-knopf-*`): jede Utility des
        Aufrufers steht in `@layer utilities` und gewinnt damit auf BEIDEN
        Breiten — ohne Doppelschreibung, und ohne dass die Voreinstellung ihre
        Breitenabhaengigkeit verliert.

        ⚠️ Innenabstand (`px-*`) und Gewicht bleiben mit `md:`-Vorsatz HIER.
        514 teilt ausdruecklich auf: Farbe je Art, Innenabstand im Verhaeltnis
        zur Hoehe, Ausrichtung und Zustaende setzt das Bauteil fest; Hoehe,
        Radius und Schriftgroesse darf der Aufrufer setzen. Dass die einen
        unueberschreibbar sind, ist Absicht.

        ⚠️ Genau DREI Abweichungen sind erlaubt (514): Chip 32/Radius 99 auf
        beiden Breiten · Bewerberseiten 52/Schrift 16 auf beiden Breiten (515,
        S8 — iOS zoomt unter 16 px) · Symbolknopf quadratisch (513). „Wer eine
        vierte braucht, meldet sie als Befund, statt sie im Aufrufer zu
        schreiben."

        ⚠️ `lg` IST DIE HAUPTHANDLUNG. Es gab die Groesse mit 44 px; 506 laesst
        am Rechner nur 32 und 40 zu. Statt sie zu streichen (79 Aufrufstellen)
        faellt sie mit `default` zusammen — dieselben vier Masse, ein Name
        weniger im Kopf.

        ⚠️ `icon` FOLGT DER HOEHE der jeweiligen Stufe. Antwort 97 (02.09.2026)
        hatte 38 px bestaetigt („die Trefferflaeche zaehlt, nicht das Bild") —
        506 misst spaeter und laesst 38 nicht mehr zu.

        ✅ Der Widerspruch war in Runde 29 gemeldet und ist mit Anweisung 513
        (Korrekturpaket 80, 10.09.2026) ENTSCHIEDEN — und zwar so, wie es hier
        stand: „506 bestreitet 97 nicht, es beantwortet eine andere Frage. 97
        sagte «die Trefferflaeche zaehlt, nicht das Bild» — dieser Satz gilt
        weiter, und 40 px erfuellen ihn besser als 38. Die 38 waren nie ein
        Mass mit Begruendung, sondern der Wert, den der Bau damals hatte."
        Und ausdruecklich: „Kein Sondermass fuer Symbolknoepfe — ein Knopf ohne
        Wort ist kein anderer Knopf, er hat nur weniger Inhalt."
      */
      size: {
        default:
          'ds-knopf-radius ds-knopf-hoehe-haupt ds-knopf-schrift-haupt px-4 font-medium',
        lg: 'ds-knopf-radius ds-knopf-hoehe-haupt ds-knopf-schrift-haupt px-4 font-medium',
        sm: 'ds-knopf-radius ds-knopf-hoehe-neben ds-knopf-schrift-neben px-3 font-medium',
        icon: 'ds-knopf-radius ds-knopf-quadrat',
      },
    },
    /*
      #1329 T18 — die Vorgabe ist `quiet`, nicht `primary`. „Eine
      Akzentfläche je Fläche": wer die Art nicht nennt, bekommt die ruhige,
      und die Hauptaktion muss bewusst gewählt werden. Bis T18 war die
      Vorgabe `primary` — so trugen 244 Knöpfe ohne `variant` die
      Akzentfläche, ohne es zu sagen. Die Aliase `default`, `outline`,
      `secondary` und `destructive` sind gestrichen; der Typ lässt sie nicht
      mehr zu. `check:ui-einheit` (`knopf-alias`) verlangt die Art trotzdem
      an jedem Aufruf ausdrücklich.
    */
    defaultVariants: {
      variant: 'quiet',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends
    React.ComponentPropsWithRef<'button'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Regel 532 (Blatt 11 · Bauteil 5): eine gesperrte Handlung bleibt
   * sichtbar und nennt ihren Grund DANEBEN — ein Satz in Grau, keine
   * Sprechblase; grau, nicht rot, und nie über Deckkraft (A2). Die vier
   * Anlässe: gesperrte Adresse · fehlende Einwilligung · fehlendes Recht ·
   * laufender Vorgang. NICHT die Geräte-Sperre (Befund 454, eigene Zeile).
   *
   * `weg`: steht ein Weg offen, ist der Grund ein Verweis im Fliesstext —
   * kein zweiter Knopf. Ohne `weg` ist er nur Text („nie ein toter Knopf",
   * 428).
   *
   * #1329 T18: hier ist `GehtHierNicht` aufgegangen — dieselbe Rolle, zwei
   * Bauteile. Der Grund trägt `--foreground-muted` und 14 px: ein Satz, der
   * GELESEN wird, keine Beschriftung (Blatt 11, Tokens).
   */
  gesperrt?: {
    grund: React.ReactNode;
    weg?: { label: React.ReactNode; onClick: () => void };
  };
}

const Button: React.FC<ButtonProps> = ({
  className,
  variant,
  size,
  asChild = false,
  gesperrt,
  ...props
}) => {
  const Comp = asChild ? Slot.Root : 'button';
  const knopf = (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
      disabled={gesperrt ? true : props.disabled}
    />
  );

  if (!gesperrt) return knopf;

  /*
    Ein vollbreiter Knopf (`w-full`, die Kauf-Zone der Kassen) hat kein
    „daneben" — dort steht der Grund darunter, linksbündig. In der
    `inline-flex`-Hülle bliebe der Knopf so breit wie sein Inhalt.
  */
  const vollbreit = /(^|\s)w-full(\s|$)/.test(className ?? '');

  return (
    <span
      data-test="geht-hier-nicht"
      className={cn(
        'inline-flex flex-wrap items-center gap-x-3 gap-y-1.5',
        vollbreit && 'flex w-full flex-col items-stretch',
      )}
    >
      {knopf}
      <span
        className="text-foreground-muted text-sm"
        data-test="geht-hier-nicht-grund"
      >
        {gesperrt.grund}
        {gesperrt.weg ? (
          <>
            {' · '}
            {/*
              Die Trefferfläche ist der KASTEN (`min-h-11` am Telefon), kein
              `::after` — S9d mass mit Pseudo-Element 20 px.
            */}
            <button
              type="button"
              onClick={gesperrt.weg.onClick}
              data-test="geht-hier-nicht-weg"
              className="text-primary inline-flex min-h-11 items-center underline-offset-4 hover:underline md:not-any-pointer-coarse:min-h-0"
            >
              {gesperrt.weg.label}
            </button>
          </>
        ) : null}
      </span>
    </span>
  );
};

Button.displayName = 'Button';

export { Button, buttonVariants };
